/**
 * Hybrid Text Component
 * Automatically chooses between Tailwind and styled-components based on props.
 *
 * Note: `hasStyledProps` does not include typography props, but Text's primary
 * API prop is `fontSize` (mapped to the `typeScale` system prop). To preserve
 * font sizing we additionally route to the styled-components path whenever any
 * typography prop is present, so those styles are never silently dropped.
 */

import * as React from "react";
import Container from "./styles"; // Styled-components version
import { TextTailwind } from "./TextTailwind"; // Tailwind version
import { hasStyledProps } from "@sproutsocial/seeds-react-system-props";
import type { TypeTextProps } from "./TextTypes";

const TYPOGRAPHY_KEYS = [
  "fontSize",
  "typeScale",
  "fontWeight",
  "textAlign",
  "lineHeight",
  "isItalicized",
  "fontFamily",
  "fontStyle",
] as const;

const hasTextStyledProps = (props: Record<string, unknown>): boolean =>
  hasStyledProps(props) ||
  TYPOGRAPHY_KEYS.some((key) => props[key] !== undefined);

const TextHybrid = React.forwardRef<HTMLSpanElement, TypeTextProps>(
  ({ fontSize, children, qa, color, ...rest }, ref) => {
    const qaText = typeof children === "string" ? children : undefined;

    if (hasTextStyledProps({ fontSize, color, ...rest })) {
      return (
        <Container
          ref={ref}
          typeScale={fontSize}
          data-qa-text={qaText}
          // TODO: fix this type since `color` should be valid here. TS can't resolve the correct type.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          color={color}
          {...qa}
          {...rest}
        >
          {children}
        </Container>
      );
    }

    // Use Tailwind version (preferred - better performance)
    return (
      <TextTailwind ref={ref} data-qa-text={qaText} {...qa} {...rest}>
        {children}
      </TextTailwind>
    );
  }
);

TextHybrid.displayName = "Text";

export default TextHybrid;
